/**
 * Column Configuration Modal
 * 
 * Excel-like column management interface:
 * - Show/hide columns with checkboxes
 * - Drag and drop reordering
 * - Reset to defaults
 */

import React, { useState } from 'react';
import { 
  X, 
  Eye, 
  EyeOff, 
  GripVertical, 
  RotateCcw,
  Settings,
  Check
} from 'lucide-react';
import type { ColumnDefinition, TableDataRow } from '../types/table';

interface ColumnConfigModalProps<T extends TableDataRow = TableDataRow> {
  isOpen: boolean;
  onClose: () => void;
  columns: ColumnDefinition<T>[];
  onToggleVisibility: (columnId: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onResetToDefault: () => void;
}

interface DragItem {
  index: number;
  id: string;
}

export const ColumnConfigModal = <T extends TableDataRow = TableDataRow>({
  isOpen,
  onClose,
  columns,
  onToggleVisibility,
  onReorder,
  onResetToDefault
}: ColumnConfigModalProps<T>) => {
  const [draggedItem, setDraggedItem] = useState<DragItem | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  if (!isOpen) return null;

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    const item: DragItem = { index, id: columns[index].id };
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.style.opacity = '1';
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    
    if (draggedItem && draggedItem.index !== index) {
      onReorder(draggedItem.index, index);
    }
    
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const visibleCount = columns.filter(col => col.visible).length;
  const totalCount = columns.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-96 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Kolonneinnstillinger</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stats */}
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              <Check className="w-4 h-4 inline mr-1 text-green-600" />
              {visibleCount} av {totalCount} kolonner vises
            </span>
            <button
              onClick={onResetToDefault}
              className="flex items-center gap-1 text-teal-600 hover:text-teal-700 text-xs font-medium"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Tilbakestill
            </button>
          </div>
        </div>

        {/* Column List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {columns.map((column, index) => (
            <div
              key={column.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              className={`flex items-center gap-3 p-2 rounded border cursor-move transition-colors ${
                dragOverIndex === index 
                  ? 'border-teal-500 bg-teal-50' 
                  : 'border-gray-200 hover:bg-gray-50'
              } ${
                draggedItem?.id === column.id ? 'opacity-50' : ''
              }`}
            >
              {/* Drag Handle */}
              <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />

              {/* Visibility Toggle */}
              <button
                onClick={() => onToggleVisibility(column.id)}
                className={`flex-shrink-0 p-1 rounded transition-colors ${
                  column.visible 
                    ? 'text-teal-600 hover:text-teal-700' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {column.visible ? (
                  <Eye className="w-4 h-4" />
                ) : (
                  <EyeOff className="w-4 h-4" />
                )}
              </button>

              {/* Column Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium truncate ${
                    column.visible ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    {column.label}
                  </span>
                  <span className="text-xs text-gray-400 ml-2">
                    {column.width}px
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-500 capitalize">
                    {column.type}
                  </span>
                  {column.sortable && (
                    <span className="text-xs text-teal-600 bg-teal-100 px-1.5 py-0.5 rounded">
                      Sorterbar
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-500 text-center">
            Dra kolonner for å endre rekkefølgen • Klikk øyet for å skjule/vise
          </div>
        </div>
      </div>
    </div>
  );
};